import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { isEmpty } from 'lodash-es';
import { Action } from 'redux';

import lang from '../../../lib/language';
import { News } from '../../../lib/models/news';
import { RootState } from '../../../lib/store';
import { getLatestNews as serviceGetLatestNews, getNewsForSymbol as serviceGetNewsForSymbol } from '../../../lib/store/news';
import { isLoggedIn as serviceIsLoggedIn } from '../../../lib/store/selectors';
import GatewayWebsocketState from '../../../lib/store-util/GatewayWebsocketState';

import LibraryConfig from '../../../configLib';
import { parseRssFeed } from '../../../services/CapitalRSS/CapitalRSS.service';
import { RssItem } from '../../../services/CapitalRSS/CapitalRSS.types';
import LatestNewsContent from '../LatestNewsContent/LatestNewsContent';
import NewsCard from '../NewsCard/NewsCard';
import SkeletonNewsCard from '../SkeletonNewsCard/SkeletonNewsCard';

import styles from './LatestNews.module.scss';

const {
  login: gwEnabled,
  latestNews: gwLatestNewsEnabled,
  newsForSymbol: gwNewsForSymbolEnabled,
} = LibraryConfig.gatewayEnabled;

type OwnProps = {
  selectedSymbol?: string;
  smallTitle?: boolean;
  isExtended?: boolean;
};
type Props = OwnProps & ReduxProps;

const LatestNews = (
  {
    latestNews,
    newsForSymbols,
    isLoggedIn,
    isLoading,
    getLatestNews,
    getNewsForSymbol,
    selectedSymbol,
    smallTitle,
    isExtended,
  }: Props,
): JSX.Element => {
  const SKELETON_ITEMS = 4;
  const [isMounted, setIsMounted] = useState(false);
  const [newsCards, setNewsCards] = useState<JSX.Element[]>([]);
  const [hasTriedLoadingRss, setHasTriedLoadingRss] = useState(false);
  const selectedSymbolNews: News[] = selectedSymbol ? newsForSymbols[selectedSymbol] : [];
  const { isConnected: isGWConnected, isLoggedin: isGWLoggedin } = GatewayWebsocketState.use();
  const isNewsAvailable: boolean = selectedSymbol ? !isEmpty(newsForSymbols[selectedSymbol]) : !isEmpty(latestNews);

  useEffect(() => { // for symbol detail
    if (!selectedSymbol) {
      return;
    }

    if (isLoading) {
      setNewsAsSkeletonLoader();
      return;
    }

    const data: News[] = newsForSymbols[selectedSymbol] || [];
    setNewsCards(data?.map((item: News) => <NewsCard key={item.id} item={item} />));
  }, [latestNews, newsForSymbols, selectedSymbol, isLoading]);// eslint-disable-line react-hooks/exhaustive-deps

  const setNewsAsSkeletonLoader = ():void => {
    setNewsCards(Array.from({ length: SKELETON_ITEMS }, (_, index) =>
      <SkeletonNewsCard isExtended={isExtended} key={index} />));
  };

  useEffect(() => { // for other screens
    if (selectedSymbol || hasTriedLoadingRss) {
      return;
    }

    const loadNews = async () => {
      setHasTriedLoadingRss(true);
      setNewsAsSkeletonLoader();
      const data = await parseRssFeed();
      setNewsCards(data?.map((item: RssItem) => <NewsCard key={item.id} item={item} />));
    };

    loadNews();
  }, [selectedSymbol, hasTriedLoadingRss]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      {!isExtended && (
        <h6 className={styles.wrapperTitle}>
          {smallTitle ? lang.symbolDetailsTabNews() : lang.commonDiscoverNews()}
        </h6>
      )}
      <LatestNewsContent
        gwEnabled={gwEnabled}
        newsCards={newsCards}
        isMounted={isMounted}
        isLoggedIn={isLoggedIn}
        selectedSymbol={selectedSymbol}
        isNewsAvailable={isNewsAvailable && isGWConnected && isGWLoggedin}
        isLoading={isLoading}
        hasInitialDataForSelectedSymbol={!isEmpty(selectedSymbolNews)}
        gwLatestNewsEnabled={gwLatestNewsEnabled}
        gwNewsForSymbolEnabled={gwNewsForSymbolEnabled}
        getLatestNews={getLatestNews}
        getNewsForSymbol={getNewsForSymbol}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isLoggedIn: serviceIsLoggedIn(state),
  newsForSymbols: state.news.newsForSymbols,
  latestNews: state.news.latestNews,
  isLoading: state.news.isLoading,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getLatestNews: (page?: number, pageSize?: number) =>
    dispatch(serviceGetLatestNews({ page, pageSize })),
  getNewsForSymbol: (symbol: string, page?: number, pageSize?: number) =>
    dispatch(serviceGetNewsForSymbol({ symbol, page, pageSize })),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(LatestNews);
